// Base component (no leaf-theme override): the htmlLoader is a bare div with no
// CSS-framework dependency, so every theme inherits this runtime unchanged.
const component = require('../component/component');

// Fetches HTML (from a controller action or a direct url — the server already built
// the url, see htmlLoader.php get_link()), injects it into its content subelement
// and inits the components it contains.
//
// Every load clear()s first: hm.app.initComponents() only WARNS on a duplicate id
// and then overwrites hm.app.components[id], so re-loading without deregistering
// would orphan the previous render's components in the registry.
const htmlLoader = Object.assign({}, component, {
    notSubmittable: true,

    //request contract, read off the element by readZAttributes()
    link: '',
    method: 'GET',
    postParams: {},
    isInternal: true,
    autoLoad: true,
    contentSelector: false,

    //Object.assign replaces the whole map, so the base's validationMessage is redeclared
    subelements: {
        validationMessage: null,
        content: null,
        loading: null,
        error: null
    },

    loadedIds: [],
    busy: false,

    // Loading from inited() rather than init(): hm.app.initComponents() runs every
    // component's init() before any inited(), so the whole page is registered before
    // we start injecting into it. hm.app.newComponent() calls both, so a loader added
    // to the page at runtime auto-loads too.
    inited: function () {
        if (this.autoLoad) {
            this.load();
        }
    },

    // Public re-fetch. Reachable from an action configured with
    // 'method' => 'triggerLinkedComponent', 'controller' => '<loader html id>',
    // 'action' => 'refresh'.
    refresh: function () {
        return this.load();
    },

    load: async function () {
        if (this.busy) {
            return false;
        }

        if (!this.link) {
            this.hm.warn('htmlLoader without a link', this.id, this.element);
            return false;
        }

        this.busy = true;

        // First load: show the loading block. A refresh over existing content: block it
        // instead, so what is on screen stays put under the theme's blockUI overlay.
        let hasContent = this.hasSubelement('content') && this.subelements.content.childNodes.length > 0;

        if (hasContent) {
            this.block('htmlLoader');
        } else {
            this.showSub('loading');
        }

        this.hideSub('error');

        let result = await this.hm.app.fetchHtml(
            this.link,
            this.method === 'POST' ? this.postParams : {},
            this.method,
            this.isInternal
        );

        let nodes = false;
        let reason = false;

        if (result === false) {
            // fetch itself rejected: network down, DNS, or a CORS block on an external link
            reason = 'network';
        } else if (!result.ok) {
            reason = 'http';
        } else if (this.isInternal && result.redirected) {
            // The html output engine does not set vars->ajax, so router::redirect() sends a
            // real Location: 303 that fetch follows silently — an expired session would hand
            // us the login page. Never inject it, and never navigate either: session expiry
            // is owned by hm.session's checkAuth poller, and hijacking the page from a
            // background region load would be worse than showing an error here.
            reason = 'redirected';
        } else {
            nodes = this.parseResponse(result.text);

            if (nodes === false) {
                reason = 'selector';
            } else if (nodes.length === 0) {
                reason = 'empty';
            }
        }

        if (reason !== false) {
            this.hm.warn('htmlLoader failed to load', this.id, reason, this.link, result);
            this.showSub('error');
        } else {
            // Failures deliberately leave the previous render on screen; only a success replaces it.
            this.clear();
            this.subelements.content.append(...nodes);
            this.loadedIds = this.hm.app.initComponents(this.subelements.content);
        }

        this.hideSub('loading');
        this.release('htmlLoader');
        this.busy = false;

        this.element.dispatchEvent(new CustomEvent(reason === false ? 'zitHtmlLoaded' : 'zitHtmlLoadFailed', {
            bubbles: true,
            detail: { loader: this, ids: this.loadedIds, reason: reason, result: result }
        }));

        return reason === false;
    },

    // Returns the nodes to inject, or false when contentSelector matched nothing.
    // The parser puts a bare fragment into body just as it puts a full document's
    // content there, so body children covers both shapes.
    parseResponse: function (html) {
        let doc = new DOMParser().parseFromString(html, 'text/html');
        let root = doc.body;

        if (this.contentSelector) {
            root = doc.querySelector(this.contentSelector);

            if (root === null) {
                return false;
            }
        }

        // A DOMParser-created <script> never executes on insertion; dropping them avoids
        // the illusion that the loaded markup's scripts ran.
        root.querySelectorAll('script').forEach((script) => { script.remove(); });

        if (root.querySelector('form') !== null) {
            this.hm.warn('htmlLoader: loaded content contains a <form>, which will nest inside the host form', this.id);
        }

        this.warnDuplicateIds(root);

        return Array.from(root.childNodes);
    },

    // initComponents only warns on a duplicate id and then takes over the registry entry,
    // and component.init() resolves its holder/subelements with document-wide queries — so
    // an overlapping fragment silently cross-wires the host page. Say it loudly here, with
    // the ids, because the symptom (a host component that stopped responding) is far away
    // from the cause. Loading a fragment too wide for the page is what causes it: narrow it
    // with contentSelector, or point the loader at an action that renders a partial.
    warnDuplicateIds: function (root) {
        let duplicates = [];

        root.querySelectorAll('[data-zit-component]').forEach((element) => {
            if (element.id && typeof this.hm.app.components[element.id] !== typeof undefined) {
                duplicates.push(element.id);
            }
        });

        if (duplicates.length) {
            this.hm.warn('htmlLoader: loaded content re-uses component ids already on the page', this.id, duplicates);
        }
    },

    // Empty the content and deregister everything that was in it.
    clear: function () {
        this.removeSubcomponents();

        if (this.hasSubelement('content')) {
            this.subelements.content.replaceChildren();
        }
    },

    // Called by hm.app.removeComponents() when the loader itself goes away — and by
    // clear() before every injection. elementSubComponents reports the loader too,
    // hence the own-id skip; it is authoritative in a way loadedIds is not, since it
    // also catches components a nested container added after our load.
    removeSubcomponents: function () {
        let subComponentIds = this.hm.app.elementSubComponents(this.element).filter((componentId) => {
            return componentId !== this.id;
        });

        this.hm.app.removeComponents(subComponentIds);

        this.loadedIds = [];
    },

    getValue: function () {
        return null;
    },

    formPostData: function (formPostData) {
        return formPostData;
    }
});

module.exports = htmlLoader;
